import { useCallback, useEffect, useRef, useState } from "react";
import {
	askIfListenersLoaded,
	baseTransKey,
	handleExtensionBridge,
	sendGetProctoringSessionId,
	startPreAssessmentCheck,
	stopProctoring,
} from "../takeAssessmentHelper";
import { AssessmentInfo } from "@verity/api-client/types";
import { retry } from "../../../../helpers/util";
import { coreToast } from "@verity/components/core";
import i18n from "../../../../i18n";

type SubmitRefReturn = {
	success: boolean;
	totalPoints?: number;
	earnedPoints?: number;
};

export default function usePreAssessmentCheck() {
	const [proctoringSessionId, setProctoringSessionId] = useState<string | undefined>(undefined);
	const [hasStartedPreCheck, setHasStartedPreCheck] = useState<boolean>(false);
	const [isStoppingProctoring, setIsStoppingProctoring] = useState<boolean>(false);
	const [hasListenersLoaded, setHasListenersLoaded] = useState<boolean>(false);
	const [isExempted, setIsExempted] = useState<boolean>(false);
	const [showFinishUI, setShowFinishUI] = useState<boolean>(false);

	/** Score set by TakeQuizPage when the student submits. */
	const [quizScore, setQuizScore] = useState<{ earned: number; total: number } | null>(null);

	const submitRef = useRef<() => Promise<SubmitRefReturn>>();

	/**The function passed in gets called when "submit" is triggered. If true is returned, will stop the proctoring session. */
	const registerSubmitFn = useCallback((fn: () => Promise<SubmitRefReturn>) => {
		submitRef.current = fn;
	}, []);

	// Assuming all the content scripts and lockdown code checked for extension/auth/live session etc
	const handleStartPreCheck = (assessmentInfo: AssessmentInfo | undefined) => {
		if (!assessmentInfo) return;
		setHasStartedPreCheck(true);
		startPreAssessmentCheck(assessmentInfo);
	};

	const handleSubmitAssessment = useCallback(async (forceStop = false) => {
		if (submitRef.current) {
			try {
				setIsStoppingProctoring(true);

				const updateScores = (res: SubmitRefReturn) => {
					if (res.earnedPoints !== undefined && res.totalPoints !== undefined) {
						setQuizScore({ earned: res.earnedPoints, total: res.totalPoints });
					}
				};

				const attemptSubmission = async () => {
					const res = await submitRef.current?.();
					const success = res?.success;
					if (!success) {
						throw new Error("Submission returned false");
					}
					updateScores(res);
					return true;
				};

				if (forceStop) {
					try {
						// Try up to 3 times
						await retry(attemptSubmission, { maxRetries: 3 });
					} catch (retryError) {
						console.error(retryError);
					} finally {
						// stop proctoring regardless of submission success, since this is a forced stop (e.g. time expired)
						stopProctoring();
					}
				} else {
					// Standard submission (no retries)
					const res = await submitRef.current();
					const success = res?.success;
					if (success) {
						updateScores(res);
						stopProctoring();
					} else {
						throw new Error("Submission failed");
					}
				}
			} catch (e) {
				console.error(e);
				setIsStoppingProctoring(false);
			}
		}
	}, []);

	const handleFinish = () => {
		setProctoringSessionId(undefined);
		setShowFinishUI(false);
	};

	const handlePreAssessmentCheckResponse = (event: MessageEvent) => {
		return handleExtensionBridge({
			event,
			onLoaded: (isExempted) => {
				setHasListenersLoaded(true);
				setIsExempted(!!isExempted);
			},
			onSuccess: (proctoringSessionId) => {
				setProctoringSessionId(proctoringSessionId);
				setHasStartedPreCheck(false);
			},
			onError: (errorType, errorDetails) => {
				console.error("Pre-assessment check error:", { errorType, errorDetails });
				coreToast.error(i18n.t(`${baseTransKey}.error.proctoringStartFail`) as string);
				setHasStartedPreCheck(false);
			},
			onProctoringSessionIdResponse: (proctoringSessionId) => setProctoringSessionId(proctoringSessionId),
			onStopProctoringResponse: () => {
				setShowFinishUI(true);
				setHasStartedPreCheck(false);
				setIsStoppingProctoring(false);
			},
		});
	};

	const listenersLoadedRef = useRef(hasListenersLoaded);
	useEffect(() => {
		listenersLoadedRef.current = hasListenersLoaded;
	}, [hasListenersLoaded]);

	// Adds listeners to listen for messages from the extension
	useEffect(() => {
		window.addEventListener("message", handlePreAssessmentCheckResponse);

		let listenersLoadedCheckInterval: ReturnType<typeof setInterval> | undefined;
		if (!listenersLoadedRef.current) {
			askIfListenersLoaded();
			listenersLoadedCheckInterval = setInterval(() => {
				if (listenersLoadedRef.current) {
					clearInterval(listenersLoadedCheckInterval);
				} else {
					askIfListenersLoaded();
				}
			}, 1000);
		}
		return () => {
			if (listenersLoadedCheckInterval) {
				clearInterval(listenersLoadedCheckInterval);
			}
			window.removeEventListener("message", handlePreAssessmentCheckResponse);
		};
	}, []);

	useEffect(() => {
		// Ask for proctoring session id from extension/lockdown if not set
		if (!proctoringSessionId && hasListenersLoaded) {
			sendGetProctoringSessionId();
		}
	}, [proctoringSessionId, hasListenersLoaded]);

	return {
		proctoringSessionId,
		hasStartedPreCheck,
		hasListenersLoaded,
		handleStartPreCheck,
		handleSubmitAssessment,
		isStoppingProctoring,
		showFinishUI,
		isExempted,
		handleFinish,
		registerSubmitFn,
		quizScore,
	};
}

export type PreAssessmentCheckReturn = ReturnType<typeof usePreAssessmentCheck>;
